import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { subscribeToPanelData } from '../../services/PanelDataService';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';
import {
  resolveMissionDates,
  resolveParticipantDatePeriods,
} from '../ems/ParticipantDetailsSidebar';

export const FORM_TABS = [
  { key: 'registration', label: 'Registration', id: '68c7eefbd44999eedb541043' },
  { key: 'accreditation', label: 'Accreditation', id: '68e6505866f25479291b933f' },
  { key: 'travel', label: 'Travel', id: '68e781408e1384b2c283c3af' },
  { key: 'accommodation', label: 'Accommodation', id: '68e784018e1384b2c283c3bb' },
  { key: 'guest', label: 'Guest', id: '68e785cb8e1384b2c283c3c5' },
];

/** Forms an Athlete never sees in the portal (tabs, next-tab navigation, direct URLs). */
export const ATHLETE_HIDDEN_FORM_KEYS = ['accreditation', 'travel', 'accommodation'];

/** Next form tab after a successful submit (skips athlete-hidden forms). */
export const getNextFormTab = (currentKey, isAthlete = false, hideGuestTab = false) => {
  const visibleTabs = FORM_TABS.filter((tab) => {
    if (hideGuestTab && tab.key === 'guest') return false;
    if (isAthlete && ATHLETE_HIDDEN_FORM_KEYS.includes(tab.key)) return false;
    return true;
  });
  const currentIndex = visibleTabs.findIndex((tab) => tab.key === currentKey);
  if (currentIndex < 0 || currentIndex >= visibleTabs.length - 1) return null;
  return visibleTabs[currentIndex + 1];
};

const firstText = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim())?.trim() || '';

const objectLabel = (value, keys) => {
  if (!value || typeof value !== 'object') return '';
  return firstText(...keys.map((key) => value[key]));
};

/**
 * Portal header avatar = CRM profile / editProfilePhoto only.
 * Never use person_data.photo (accreditation badge → S3 Accreditation/2028).
 */
const resolveHeaderProfileImage = (personData, person, apiUrl) => {
  const edit = personData?.editProfilePhoto;
  const editEntry = Array.isArray(edit) ? edit[0] : edit;
  const editUrl =
    typeof editEntry === 'string'
      ? editEntry.trim()
      : String(editEntry?.url || '').trim();
  if (editUrl) return resolvePersonImageUrl(editUrl, apiUrl) || editUrl;

  const crmImage =
    person?.image ||
    personData?.profileImage ||
    '';
  return resolvePersonImageUrl(crmImage, apiUrl) || '';
};

/** Option 1c: integer percent from { completed, total }, or null when unknown. */
export const tabPercentFromProgress = (progress) => {
  const total = Number(progress?.total);
  const completed = Number(progress?.completed);
  if (!Number.isFinite(total) || total <= 0) return null;
  if (!Number.isFinite(completed) || completed < 0) return null;
  return Math.round((completed / total) * 100);
};

const emptyTabProgress = () => ({
  registration: null,
  accreditation: null,
  travel: null,
  accommodation: null,
  guest: null,
});

/** Labels + mission dates for the Details sidebar (user portal). */
export const getParticipantDetailsSummary = (participant, person) => {
  const data = participant?.person_data || {};
  const registrationStatus = participant?.registration_status || 'Added';
  const approvalStatus = participant?.approval_status || 'Pending';
  const approvalLabel =
    approvalStatus === 'Pending' ? 'Pending Approval' : approvalStatus;
  const group = objectLabel(participant?.group_id, ['group_name', 'name']) || data.groupName;
  const role = objectLabel(participant?.role_id, ['role_name', 'name']);
  const invitation = objectLabel(participant?.invitation_type_id, [
    'invitation_type',
    'invitation_type_name',
    'name',
  ]);
  const accreditation = objectLabel(participant?.accreditation_category_id, [
    'category_name',
    'accreditation_category',
    'name',
  ]);
  const missionDates = resolveMissionDates(participant);
  const { arrivalPeriods, stayPeriods } = resolveParticipantDatePeriods(participant);
  return {
    registrationStatus,
    approvalStatus,
    approvalLabel,
    group,
    role,
    invitation,
    accreditation,
    missionFrom: missionDates.from,
    missionTo: missionDates.to,
    arrivalPeriods,
    stayPeriods,
  };
};

const UserFormPrototypeHeader = ({
  activeSegment,
  canSwitchTabs,
  isAthlete,
  hideGuestTab = false,
  onTabSelect,
  onDetailsClick,
  detailsOpen = false,
  participant,
  person,
  tabProgress: tabProgressProp,
  tabs: tabsProp,
  isEditingGuest = false,
  onPrevGuest,
  onNextGuest,
  hasPrevGuest = false,
  hasNextGuest = false,
}) => {
  const [subscribedProgress, setSubscribedProgress] = useState(emptyTabProgress);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const tabs = Array.isArray(tabsProp) && tabsProp.length ? tabsProp : FORM_TABS;
  const apiUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (tabProgressProp) return undefined;
    return subscribeToPanelData((data) => {
      setSubscribedProgress({
        registration: data.registration || null,
        accreditation: data.accreditation || null,
        travel: data.travel || null,
        accommodation: data.accommodation || null,
        guest: data.guest || null,
      });
    });
  }, [tabProgressProp]);

  const tabProgress = tabProgressProp || subscribedProgress;
  const data = participant?.person_data || {};
  const firstName = firstText(
    person?.name,
    participant?.person_id?.name,
    data.givenFirstName
  );
  const lastName = firstText(
    person?.lname,
    participant?.person_id?.lname,
    data.familyLastName
  );
  const displayName = `${firstName} ${lastName}`.trim() || 'Participant';
  const crmPerson = person || participant?.person_id || null;
  const image = resolveHeaderProfileImage(data, crmPerson, apiUrl);

  useEffect(() => {
    setAvatarFailed(false);
  }, [image]);

  return (
    <div className="user-form-context-row">
      <section className="user-form-profile" aria-label="Participant summary">
        <div className="user-form-profile__identity">
          {isEditingGuest && (
            <button
              type="button"
              className="user-guest-nav__arrow"
              disabled={!hasPrevGuest}
              aria-label="Previous guest"
              title="Previous guest"
              onClick={() => onPrevGuest?.()}
            >
              &larr;
            </button>
          )}
          <div className="user-form-profile__avatar">
            {image && !avatarFailed ? (
              <img
                src={image}
                alt=""
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <FontAwesomeIcon icon={faUser} />
            )}
            <span className="user-form-profile__edit" aria-hidden="true">
              <FontAwesomeIcon icon={faPen} />
            </span>
          </div>
          <div>
            <h1>{displayName}</h1>
          </div>
          {isEditingGuest && (
            <button
              type="button"
              className="user-guest-nav__arrow user-guest-nav__arrow--next"
              disabled={!hasNextGuest}
              aria-label="Next guest"
              title="Next guest"
              onClick={() => onNextGuest?.()}
            >
              &rarr;
            </button>
          )}
          {!detailsOpen && (
            <button
              type="button"
              className="participant-details-toggle"
              onClick={() => onDetailsClick?.()}
            >
              Summary
            </button>
          )}
        </div>
      </section>

      <nav className="user-form-tabs" aria-label="Participant forms">
        <div className="user-form-tabs__track">
          {tabs.filter((tab) => {
            if (hideGuestTab && tab.key === 'guest') return false;
            if (isAthlete && ATHLETE_HIDDEN_FORM_KEYS.includes(tab.key)) return false;
            return true;
          }).map((tab) => {
            const isActive = activeSegment === tab.key;
            const isLocked = !isActive && !canSwitchTabs;
            // Always show a % badge (0 when unknown/empty) so every form has visible status.
            const realPct = tabPercentFromProgress(tabProgress?.[tab.key]);
            const displayPct = realPct == null ? 0 : realPct;
            const isComplete = realPct === 100;

            return (
              <button
                key={tab.key}
                type="button"
                className={[
                  'user-form-tab',
                  isActive ? 'user-form-tab--active' : '',
                  isLocked ? 'user-form-tab--locked' : '',
                  isComplete ? 'user-form-tab--complete' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={isLocked}
                title={
                  isLocked
                    ? 'Complete and save the current form first'
                    : tab.label
                }
                onClick={() => onTabSelect?.(tab)}
              >
                <span className="user-form-tab__label">{tab.label}</span>
                {displayPct != null && (
                  <span
                    className="user-form-tab__pct"
                    aria-label={`${displayPct} percent complete`}
                  >
                    {displayPct}%
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default UserFormPrototypeHeader;
